// LineageView — renders a context-graph's RETRIEVED→CITED provenance binding (DF-4). Presentational:
// given the binding header (verdict/confidence + counts) and the resolved citations, it shows the
// "retrieved N → cited M" relationship and a list of numbered Citation markers; opening a marker
// reveals its CitationPopover (passage + source + actions) inline. Cited chunks sort before
// merely-retrieved ones. Apps feed it data via useProvenance.
import { useState } from "react";

import { Citation } from "./Citation";
import { CitationPopover } from "./CitationPopover";
import type { CitationRef, LineageBinding } from "./types";

export interface LineageViewProps {
  binding: LineageBinding;
  citations: CitationRef[];
  onViewSource?: (citation: CitationRef) => void;
  onViewLineage?: (citation: CitationRef) => void;
}

function confidencePct(c: number | null | undefined): string | null {
  if (c === null || c === undefined) return null;
  return `${Math.round(c * 100)}%`;
}

export function LineageView({ binding, citations, onViewSource, onViewLineage }: LineageViewProps): React.ReactElement {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  // Cited first (the actually-used subset), then retrieved-but-not-cited, each stable by index.
  const ordered = [...citations].sort((a, b) => {
    if (a.chunk.role !== b.chunk.role) return a.chunk.role === "cited" ? -1 : 1;
    return a.index - b.index;
  });
  const pct = confidencePct(binding.confidence);

  return (
    <div className="flex flex-col gap-2 px-3 py-2">
      <div className="flex items-center gap-1.5">
        <span className="truncate text-sm font-medium text-[var(--color-text-primary)]" title={binding.label ?? binding.kind}>
          {binding.label ?? binding.kind}
        </span>
        <span className="rounded-[2px] bg-[var(--color-surface-muted)] px-1 py-px text-[10px] text-[var(--color-text-secondary)]">
          {binding.kind}
        </span>
        {binding.verdict && (
          <span className="rounded-[2px] bg-[var(--color-surface-muted)] px-1 py-px text-[10px] text-[var(--color-text-secondary)]">
            {binding.verdict}
          </span>
        )}
        {pct && <span className="text-[10px] text-[var(--color-text-tertiary)]">{pct} conf</span>}
      </div>

      <p className="text-xs text-[var(--color-text-secondary)]">
        retrieved <span className="font-semibold text-[var(--color-text-primary)]">{binding.retrievedCount}</span>
        {" → "}
        cited <span className="font-semibold text-[var(--color-accent)]">{binding.citedCount}</span>
      </p>

      {ordered.length === 0 ? (
        <p className="text-xs text-[var(--color-text-tertiary)]">No chunks bound to this context graph.</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {ordered.map((c) => {
            const open = openIndex === c.index;
            return (
              <li key={c.chunk.chunk_id} className="flex flex-col">
                <div className="flex items-center gap-2">
                  <Citation
                    index={c.index}
                    role={c.chunk.role}
                    active={open}
                    onActivate={() => setOpenIndex(open ? null : c.index)}
                  />
                  <span
                    className="min-w-0 flex-1 truncate text-xs text-[var(--color-text-secondary)]"
                    title={c.source?.filename ?? c.chunk.source_id}
                  >
                    {c.source?.filename ?? `source ${c.chunk.source_id.slice(0, 8)}…`}
                  </span>
                </div>
                {open && (
                  <div className="ml-6 mt-1 rounded-[2px] border border-[var(--color-border-subtle)] bg-[var(--color-surface-base)]">
                    <CitationPopover
                      citation={c}
                      onViewSource={onViewSource ? () => onViewSource(c) : undefined}
                      onViewLineage={onViewLineage ? () => onViewLineage(c) : undefined}
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
